import { Dispatch, forwardRef, SetStateAction, useEffect, useImperativeHandle, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { COLORS, TYPOGRAPHY } from '@app/constants';
import { FilterInputType, IFilter } from '@app/types';
import Button from '@app/ui/components/Button';
import FlatBtn from '@app/ui/components/FlatBtn';
import { Dropdown } from './components';
import { BreakpointsEnum } from '@app/types';
import { useLazyGetDiseasesGroupsQuery } from '@/app/store/api/common';
import { DiseaseResponseDto } from '@/app/api';
import { useRouter } from 'next/router';

export interface ImperativeHandleProps {
  getValues: () => FieldValues;
}

interface Props {
  className?: string;
  filters: IFilter[];
  onApplying?: (data: FieldValues, childAge: number | undefined, therapistAge: string | undefined) => void;
  onReset?: (data: FieldValues) => void;
  childTherapists?: boolean;
  isSupervisorTherapist?: boolean;
  childAge?: number;
  setChildAge?: Dispatch<SetStateAction<number>>;
  therapistAge?: string | undefined;
  setTherapistAge?: Dispatch<SetStateAction<string | undefined>>;
}

const Filters = forwardRef<ImperativeHandleProps, Props>(
  ({ className, onApplying, filters, onReset, childTherapists, isSupervisorTherapist, childAge, setChildAge, therapistAge, setTherapistAge }, ref) => {
    const {
      handleSubmit,
      formState: { isDirty },
      reset,
      control,
      setValue,
      getValues,
    } = useForm();

    const router = useRouter();

    const [isResetFilters, setIsResetFilters] = useState<boolean>(false);

    const [childDiseases, setChildDiseases] = useState<DiseaseResponseDto[]>([]);

    useEffect(() => {
      if (router.query?.childOld && setChildAge) {
        setChildAge(Number(router.query.childOld));
      }
    }, [router.query.childOld]);

    useEffect(() => {
      if (router.query?.therapistOld && setTherapistAge) {
        setTherapistAge(router.query.therapistOld.toString());
      }
    }, [router.query.therapistOld]);

    useEffect(() => {
      if (isDirty) setIsResetFilters(false);
    }, [isDirty]);

    useEffect(() => {
      const checkedFilters = filters.filter((item) => item.checked);

      if (!isDirty && checkedFilters.length) {
        checkedFilters.forEach((item) => {
          if (item.checked) {
            setValue(item.name, item.checked, { shouldDirty: true });
          }
        });
      }

      fetchDiseases();
    }, []);

    useImperativeHandle(ref, () => ({
      getValues: () => {
        return {
          ...getValues(),
          childOld: childAge,
          therapistOld: therapistAge,
        };
      },
    }), [getValues, childAge, therapistAge]);

    const onSubmit = (data: FieldValues) => {
      const finalData = {
        ...data,
        childOld: childAge,
        therapistOld: therapistAge,
      };
      onApplying?.(finalData, childAge, therapistAge);
      console.log('Filters finalData', finalData);
    };

    const resetFilters = () => {
      reset();
      setChildAge && setChildAge(8);
      setTherapistAge && setTherapistAge('any');
      setIsResetFilters(true);
      onReset?.(getValues());
    };

    const [fetchDiseasesGroups] = useLazyGetDiseasesGroupsQuery();

    const fetchDiseases = async () => {
      try {
        const result = await fetchDiseasesGroups({ isChild: childTherapists }).unwrap();
        const diseases = result?.[0]?.items || [];
        setChildDiseases(diseases);
      } catch (error) {
        console.error('Error fetching diseases:', error);
      }
    };

    return (
      <Root className={className} onSubmit={handleSubmit(onSubmit)}>
        <Header className="second-step">
          <Heading>Фильтры</Heading>
          <ResetButton onClick={resetFilters} type="button">
            Очистить
          </ResetButton>
        </Header>
        <List>
          {childTherapists &&
            <ListItem>
              <Dropdown control={control} isReset={isResetFilters} title='Возраст ребенка' name='childOld' inputType={FilterInputType.Range} childAge={childAge} setChildAge={setChildAge}/>
            </ListItem>
          }
          {childTherapists && childDiseases.length > 0 &&
            <ListItem>
              <Dropdown control={control} isReset={isResetFilters} title='Проблемы' name='diseases.0' options={childDiseases}/>
            </ListItem>
          }
          {childTherapists &&
          filters
          .filter(item => item.name === 'gender')
          .map((item) => (
            <ListItem key={item.name}>
              <Dropdown control={control} isReset={isResetFilters} {...item} />
            </ListItem>
          ))}
          {childTherapists &&
            <ListItem>
              <Dropdown control={control} isReset={isResetFilters} title='Возраст специалиста' name='therapistOld' inputType={FilterInputType.RangeSecond} therapistAge={therapistAge} setTherapistAge={setTherapistAge}/>
            </ListItem>
          }
          {childTherapists &&
          filters
          .filter(item => item.name === 'dayLimit' || item.name === 'priceMax')
          .map((item) => (
            <ListItem key={item.name}>
              <Dropdown control={control} isReset={isResetFilters} {...item} />
            </ListItem>
          ))}
          {( !childTherapists && !isSupervisorTherapist ) &&
          filters.map((item, index) => {
            if (index < 4) {
              return (
                <ListItem key={item.name}>
                  <Dropdown control={control} isReset={isResetFilters} {...item} />
                </ListItem>
              )
            }
          })}
          {!childTherapists &&
            <ListItem>
              <Dropdown control={control} isReset={isResetFilters} title='Возраст специалиста' name='therapistOld' inputType={FilterInputType.RangeSecond} therapistAge={therapistAge} setTherapistAge={setTherapistAge}/>
            </ListItem>
          }
          {(!childTherapists) &&
          filters.map((item, index) => {
            if (
              (
                !isSupervisorTherapist
                && index > 3
              )
              ||
              (
                isSupervisorTherapist
                && index > 2
                && index !== 4
                && index !== 7
                && index !== 8
              )
            ) {
              return (
                <ListItem key={item.name}>
                  <Dropdown control={control} isReset={isResetFilters} {...item} />
                </ListItem>
              )
            }
          })}
        </List>
        <StyledButton styleType="primary" isLarge>
          Применить
        </StyledButton>
      </Root>
    );
  },
);

Filters.displayName = 'Filters';

export default Filters;

const Root = styled.form`
  padding: 24px 20px;
  background-color: ${COLORS.grayscale0};
  border-radius: 20px;
  max-width: 100%;
  width: 100%;

  @media (max-width: ${BreakpointsEnum.sm}px) {
    padding: 0;
    max-width: 100%;
    background-color: ${COLORS.white};
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media (max-width: ${BreakpointsEnum.sm}px) {
    display: grid;
    align-items: flex-start;
    justify-content: normal;
    grid-template-columns: 1fr 1fr 1fr;
    grid-template-areas: 'ResetButton Heading .';
    padding: 24px 0 36px 0;
  }
`;

const Heading = styled.h5`
  ${TYPOGRAPHY.headline1Semibold18}
  color: ${COLORS.black};
  margin: 0;

  @media (max-width: ${BreakpointsEnum.sm}px) {
    grid-area: Heading;
    display: flex;
    justify-content: center;
  }
`;

const List = styled.ul`
  margin-top: 28px;
  margin-bottom: 28px;

  @media (max-width: ${BreakpointsEnum.sm}px) {
    padding: 0 8px 0 0;
    margin: 0 0 28px 0;
  }
`;

const ListItem = styled.li`
  &:not(:first-child) {
    margin-top: 16px;
  }
`;

const StyledButton = styled(Button)`
  width: 100%;
`;

const ResetButton = styled(FlatBtn)`
  padding-left: 4px;
  padding-right: 4px;

  &:hover {
    color: ${COLORS.primary300} !important;
  }

  @media (max-width: ${BreakpointsEnum.sm}px) {
    display: flex;
    align-self: flex-start;
    justify-content: flex-start;
    grid-area: ResetButton;
  }
`;
